'use client';

import { useEffect, useRef, useState } from 'react';

import { useAtomValue, useSetAtom } from 'jotai';

import { ResponseMode } from '@/shared-survey';
import {
  audioQueueAtom,
  AudioQueueItem,
  clearAudioQueueAtom,
  markAudioCompletedAtom,
  updateAudioInQueueAtom,
} from '@/shared-survey';
import {
  AUDIO_QUEUE_STOP_CURRENT_EVENT,
  centralAudioElementAtom,
  currentPlayingIdAtom,
} from '@/shared-ui';

import {
  driveAudioElementFromQueue,
  syncAudioQueueOnModeTransition,
} from './project-response-audio-controller.helpers';

/**
 * Centralized audio controller that manages a SINGLE Audio element for all messages.
 *
 * This hook:
 * - Creates ONE Audio element that persists across renders
 * - Monitors the queue and auto-plays next audio when ready (if AI voice is enabled AND in voice mode)
 * - Uses refs to avoid React closure issues
 * - Automatically advances to next audio when current finishes
 * - Sets Jotai atoms for audio element and currentPlayingId for global access
 */
export function useAudioQueueController(
  isAiVoiceEnabled = false,
  responseMode: ResponseMode = 'text',
  /** When true (HandsFree paused), no new playback and current audio is stopped */
  playbackPaused = false
): void {
  // Single Audio element for entire app
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Refs for callbacks to avoid closure issues
  const currentPlayingRef = useRef<string | null>(null);
  const queueRef = useRef<AudioQueueItem[]>([]);

  // Track user interaction with state to trigger re-renders
  const [hasInteracted, setHasInteracted] = useState(false);

  // Track audio IDs that failed autoplay to prevent infinite retry loop
  const failedAutoplayAttempts = useRef<Set<string>>(new Set());

  // Track previous response mode to detect voice re-enable
  const prevResponseModeRef = useRef(responseMode);

  // Jotai state - read
  const queue = useAtomValue(audioQueueAtom);
  const currentPlayingIdFromAtom = useAtomValue(currentPlayingIdAtom); // Read the atom so effect re-runs

  // Jotai state - write
  const updateAudio = useSetAtom(updateAudioInQueueAtom);
  const markCompleted = useSetAtom(markAudioCompletedAtom);
  const setCentralAudio = useSetAtom(centralAudioElementAtom);
  const setCurrentPlayingId = useSetAtom(currentPlayingIdAtom);
  const clearAudioQueue = useSetAtom(clearAudioQueueAtom);

  // Keep refs in sync with state

  useEffect(() => {
    queueRef.current = queue;
  }, [queue]);

  // Sync currentPlayingRef with atom value

  useEffect(() => {
    currentPlayingRef.current = currentPlayingIdFromAtom;
  }, [currentPlayingIdFromAtom]);

  // When voice mode is re-enabled, replay the last message's audio
  useEffect(
    () =>
      syncAudioQueueOnModeTransition({
        responseMode,
        isAiVoiceEnabled,
        playbackPaused,
        prevResponseModeRef,
        currentPlayingRef,
        queueRef,
        updateAudio,
      }),
    [responseMode, isAiVoiceEnabled, playbackPaused, updateAudio]
  );

  useEffect(() => {
    const audio = new Audio();
    audioRef.current = audio;

    setCentralAudio(audio);

    const handleEnded = () => {
      const playingId = currentPlayingRef.current;

      if (playingId) {
        markCompleted(playingId);
        currentPlayingRef.current = null;
        setCurrentPlayingId(null);
      }
    };

    const handleError = () => {
      const playingId = currentPlayingRef.current;

      if (playingId) {
        updateAudio({
          messageId: playingId,
          updates: { status: 'error' },
        });
        currentPlayingRef.current = null;
        setCurrentPlayingId(null);
      }
    };

    const handleStopCurrentAudio = () => {
      const playingId = currentPlayingRef.current;

      if (playingId) {
        audio.pause();
        audio.currentTime = 0;

        markCompleted(playingId);
        currentPlayingRef.current = null;
        setCurrentPlayingId(null);
      }
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    window.addEventListener(
      AUDIO_QUEUE_STOP_CURRENT_EVENT,
      handleStopCurrentAudio
    );

    return () => {
      window.removeEventListener(
        AUDIO_QUEUE_STOP_CURRENT_EVENT,
        handleStopCurrentAudio
      );
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);

      audio.pause();
      audio.src = '';

      audioRef.current = null;
      currentPlayingRef.current = null;

      setCentralAudio(null);
      setCurrentPlayingId(null);
      clearAudioQueue();
    };
  }, [
    markCompleted,
    updateAudio,
    setCentralAudio,
    setCurrentPlayingId,
    clearAudioQueue,
  ]);

  // Auto-play logic: play next audio when available and nothing is playing (if AI voice is enabled AND in voice mode)
  useEffect(
    () =>
      driveAudioElementFromQueue({
        audioRef,
        isAiVoiceEnabled,
        responseMode,
        playbackPaused,
        hasInteracted,
        setHasInteracted,
        failedAutoplayAttempts,
        currentPlayingRef,
        queue,
        updateAudio,
        markCompleted,
        setCurrentPlayingId,
        currentPlayingIdFromAtom,
      }),
    [
      queue,
      updateAudio,
      isAiVoiceEnabled,
      responseMode,
      playbackPaused,
      setCurrentPlayingId,
      hasInteracted,
      currentPlayingIdFromAtom,
      markCompleted,
    ]
  );
}
